import React from 'react';
import LandingDemoConversation from './LandingDemoConversation';

const CLASSIFICATION_DELAY_MS = 900;
const CLASSIFICATION_HIGHLIGHT_MS = 950;

const SIMULATION_TURNS = [
  {
    speaker: 'clinician',
    text: 'What has smoking been doing for you lately?',
  },
  {
    speaker: 'client',
    text: 'It is the one thing that helps me slow down after work, and part of me really does want a different way to reset.',
  },
  {
    speaker: 'clinician',
    text: 'So there is a real pull in both directions for you.',
  },
  {
    speaker: 'client',
    text: 'Exactly. When people push me to quit, I shut down fast and do not want to talk about it anymore.',
  },
];

const INITIAL_PROFILE = {
  persona: [
    'Feels emotionally overloaded after work.',
    'Protective of autonomy when discussing change.',
    'Ambivalent about change, but still able to imagine something better.',
  ],
  motivations: [
    {
      category: 'Desire+',
      text: 'Can imagine a future without cigarettes.',
    },
    null,
  ],
  barriers: [
    {
      category: 'Need-',
      text: 'Uses smoking to regulate stress and decompress.',
    },
    null,
  ],
};

const PROFILE_REVEALS = {
  1: {
    tone: 'motivation',
    section: 'motivations',
    itemIndex: 1,
    motivations: {
      category: 'Desire+',
      text: 'Wants a different way to recover and reset.',
    },
  },
  3: {
    tone: 'barrier',
    section: 'barriers',
    itemIndex: 1,
    barriers: {
      category: 'Reason-',
      text: 'Pushback from others triggers shutdown and resistance.',
    },
  },
};

function buildProfileState(turnIndex) {
  const profile = {
    persona: [...INITIAL_PROFILE.persona],
    motivations: [...INITIAL_PROFILE.motivations],
    barriers: [...INITIAL_PROFILE.barriers],
  };

  Object.entries(PROFILE_REVEALS).forEach(([index, reveal]) => {
    if (turnIndex >= Number(index)) {
      if (reveal.motivations) {
        profile.motivations[1] = reveal.motivations;
      }
      if (reveal.barriers) {
        profile.barriers[1] = reveal.barriers;
      }
    }
  });

  return profile;
}

function ProfileSection({ title, items, tone = 'default', visibleCount = items.filter(Boolean).length, highlightedItemIndex = null, highlightedTone = null }) {
  return (
    <div className="anonymous-landing__simulation-section">
      <h4>{title}</h4>
      <div className={`anonymous-landing__simulation-list${tone === 'persona' ? ' anonymous-landing__simulation-list--numbered' : ''}`}>
        {items.map((item, index) => {
          const itemText = typeof item === 'string' ? item : item?.text;
          const itemCategory = typeof item === 'object' ? item?.category : null;

          return (
          <div
            key={`${title}-${index}`}
            className={`anonymous-landing__simulation-list-item${tone === 'persona' ? ' anonymous-landing__simulation-list-item--persona' : ' anonymous-landing__simulation-list-item--coded'}${item ? '' : ' anonymous-landing__simulation-list-item--pending'}${highlightedItemIndex === index && highlightedTone ? ` anonymous-landing__simulation-list-item--highlight-${highlightedTone}` : ''}`}
          >
            {tone === 'persona' && (
              <span className="anonymous-landing__simulation-index">{index + 1}.</span>
            )}
            {tone !== 'persona' && (
              <span
                className={`anonymous-landing__simulation-tag${tone === 'barrier' ? ' anonymous-landing__simulation-tag--barrier' : ''}${itemCategory ? '' : ' anonymous-landing__simulation-tag--placeholder'}`}
                aria-hidden={itemCategory ? undefined : 'true'}
              >
                {itemCategory || 'Pending'}
              </span>
            )}
            <span>{itemText || 'Still being inferred from the conversation...'}</span>
          </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ClientSimulationShowcase() {
  const [activeTurnIndex, setActiveTurnIndex] = React.useState(-1);
  const [highlightState, setHighlightState] = React.useState(null);
  const highlightTimeoutRef = React.useRef(null);
  const classificationTimeoutRef = React.useRef(null);

  const visibleProfile = React.useMemo(() => buildProfileState(activeTurnIndex), [activeTurnIndex]);

  const handleTurnShown = React.useCallback((turnIndex, turn) => {
    if (turn.speaker !== 'client') {
      return Promise.resolve();
    }

    const reveal = PROFILE_REVEALS[turnIndex];

    return new Promise((resolve) => {
      if (classificationTimeoutRef.current) {
        window.clearTimeout(classificationTimeoutRef.current);
      }
      classificationTimeoutRef.current = window.setTimeout(() => {
        setActiveTurnIndex(turnIndex);
        if (reveal) {
          setHighlightState({
            turnIndex,
            tone: reveal.tone,
            section: reveal.section,
            itemIndex: reveal.itemIndex,
          });
          if (highlightTimeoutRef.current) {
            window.clearTimeout(highlightTimeoutRef.current);
          }
          highlightTimeoutRef.current = window.setTimeout(() => {
            setHighlightState(null);
            highlightTimeoutRef.current = null;
          }, CLASSIFICATION_HIGHLIGHT_MS);
        }
        classificationTimeoutRef.current = null;
        resolve();
      }, CLASSIFICATION_DELAY_MS);
    });
  }, []);

  const handleLoopReset = React.useCallback(() => {
    setActiveTurnIndex(-1);
    setHighlightState(null);
    if (highlightTimeoutRef.current) {
      window.clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = null;
    }
    if (classificationTimeoutRef.current) {
      window.clearTimeout(classificationTimeoutRef.current);
      classificationTimeoutRef.current = null;
    }
  }, []);

  React.useEffect(() => () => {
    if (highlightTimeoutRef.current) {
      window.clearTimeout(highlightTimeoutRef.current);
    }
    if (classificationTimeoutRef.current) {
      window.clearTimeout(classificationTimeoutRef.current);
    }
  }, []);

  const getTurnClassName = React.useCallback((turnIndex, turn) => {
    if (turn.speaker !== 'client' || !highlightState || highlightState.turnIndex !== turnIndex) {
      return '';
    }
    return highlightState.tone === 'motivation'
      ? 'anonymous-landing__demo-turn--highlight-motivation'
      : 'anonymous-landing__demo-turn--highlight-barrier';
  }, [highlightState]);

  return (
    <div className="anonymous-landing__simulation">
      <div className="anonymous-landing__simulation-conversation-shell">
        <p className="anonymous-landing__demo-card-title">Human MI transcript</p>
        <LandingDemoConversation
          turns={SIMULATION_TURNS}
          onLoopReset={handleLoopReset}
          onTurnShown={handleTurnShown}
          getTurnClassName={getTurnClassName}
        />
      </div>

      <div className="anonymous-landing__simulation-profile-column">
        <div className="anonymous-landing__simulation-profile-stack">
          <ProfileSection title="Persona" items={visibleProfile.persona} tone="persona" visibleCount={visibleProfile.persona.filter(Boolean).length} />
          <ProfileSection title="Motivations" items={visibleProfile.motivations} visibleCount={visibleProfile.motivations.filter(Boolean).length} highlightedItemIndex={highlightState?.section === 'motivations' ? highlightState.itemIndex : null} highlightedTone={highlightState?.section === 'motivations' ? highlightState.tone : null} />
          <ProfileSection title="Barriers" items={visibleProfile.barriers} tone="barrier" visibleCount={visibleProfile.barriers.filter(Boolean).length} highlightedItemIndex={highlightState?.section === 'barriers' ? highlightState.itemIndex : null} highlightedTone={highlightState?.section === 'barriers' ? highlightState.tone : null} />
        </div>
      </div>
    </div>
  );
}
